import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth';
import type { ContactRequest, FeedbackCategory, RequestType } from '../types';

interface FeedbackFormData {
  feedback_category_id: string | null;
  request_type: RequestType;
  subject?: string;
  message: string;
  first_name: string;
  last_name: string;
}

export function useFeedbackForm() {
  const { user } = useAuth();

  const submitMutation = useMutation({
    mutationFn: async (data: FeedbackFormData) => {
      if (!user) throw new Error('Not authenticated');

      const { data: result, error } = await supabase
        .from('contact_requests')
        .insert({
          user_id: user.id,
          feedback_category_id: data.feedback_category_id,
          request_type: data.request_type,
          first_name: data.first_name,
          last_name: data.last_name,
          email: user.email || '',
          subject: data.subject || null,
          message: data.message,
          status: 'pending'
        })
        .select()
        .single();

      if (error) throw error;
      return result as ContactRequest;
    },
  });

  return {
    submit: submitMutation.mutateAsync,
    isSubmitting: submitMutation.isPending,
    isSuccess: submitMutation.isSuccess,
    error: submitMutation.error,
    reset: submitMutation.reset,
  };
}

// Fetch feedback categories (app areas)
export function useFeedbackCategories() {
  return useQuery({
    queryKey: ['feedback-categories'],
    queryFn: async (): Promise<FeedbackCategory[]> => {
      const { data, error } = await supabase
        .from('feedback_categories')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;
      return data || [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}
